import { useMutation, useQuery } from "@apollo/client/react";
import { EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ArrowPathIcon, PlusCircleIcon } from "@heroicons/react/24/solid";
import { ColumnDef, getCoreRowModel } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  Avatar,
  ButtonDots,
  itemClasses,
  VehicleThumb,
  Watchers,
} from "../../../../../components/appearance";
import { ErrorFallback, Head } from "../../../../../components/core";
import { Button } from "../../../../../components/form";
import { LimitBy, TableCursor } from "../../../../../components/table";
import {
  GET_SERVICEREMAINDERS,
  REMOVE_SERVICEREMAINDER,
  renderStatus,
} from "../../../../../graphql/fleets/vehicles/pages/service-reminder";
import { VehicleServiceReminder } from "../../../../../graphql/fleets/vehicles/pages/service-reminder";
import { AlertModal, AlertType, useAlert } from "../../../../../hooks/useAlert";
import { classNames, formatDate } from "../../../../../utils";
import { AuthShield } from "../../../../auth/core";

const ServiceReminderList = ({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) => {
  const { t } = useTranslation();
  const [refetching, setRefetching] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<LimitBy>(LimitBy.L3);

  const { data, loading, error, refetch } = useQuery<{
    fetchServiceRemainders: VehicleServiceReminder[] | null;
  }>(GET_SERVICEREMAINDERS);

  const serviceReminders = useMemo<VehicleServiceReminder[]>(() => {
    const updatedServiceReminder = data?.fetchServiceRemainders?.length
      ? data?.fetchServiceRemainders.map((item) => {
          return {
            ...item,
            lastCompletionDate: formatDate(item.lastCompletionDate),
            nextDueDate: formatDate(item.nextDueDate),
            createdAt: formatDate(item.createdAt),
          };
        })
      : [];
    return updatedServiceReminder;
  }, [data]);

  const [deleteVehicleServiceReminder] = useMutation(REMOVE_SERVICEREMAINDER);
  const handleDelete = async (id: string) => {
    deleteVehicleServiceReminder({
      variables: {
        id: Number(id),
      },
    })
      .then(async ({ data }) => {
        if (data?.vehicleServiceRemainderDelete) {
          await refetch();
          toast.success(`Vehicle service reminder deleted successfully`);
        } else {
          toast.error("Something went wrong, please try again later");
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<VehicleServiceReminder, any>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "vehicle.name",
        header: "Vehicle",
        cell: (props) => (
          <VehicleThumb
            name={props.row.original.vehicle.name}
            status={props.row.original.vehicle.status}
            vehicleImageUrl={props.row.original.vehicle.vehicleImageUrl}
            make={props.row.original.vehicle.make}
            model={props.row.original.vehicle.model}
            year={props.row.original.vehicle.year}
            operator={props.row.original.vehicle.operator}
            id={parseInt(props.row.original.vehicle.id)}
            fuelUnit={null}
            measurementUnit={null}
            primaryMeter={null}
          />
        ),
      },
      {
        accessorKey: "vehicleServiceTask.name",
        header: "Task Name",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (props) => renderStatus(props.row.original.status),
      },
      {
        accessorKey: "nextDueDate",
        header: "Next Due",
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
      },
      {
        accessorKey: "lastCompletionDate",
        header: "Last Completed",
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
      },
      {
        accessorKey: "watchers",
        header: "Watchers",
        cell: (props) => (
          <Watchers
            caption={`Watchers of ${props.row.original.vehicle.name} service reminder - ${props.row.original.vehicleServiceTask.name}`}
            data={props.row.original.watchers}
          />
        ),
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableHiding: false,
        enableGlobalFilter: false,
        size: 220,
        cell: (props) => (
          <ButtonDots>
            <AuthShield access={["read-vehicles"]}>
              <Button
                variant="text"
                className={itemClasses}
                type="link"
                href={`/fleets/vehicles/service-reminders/${props.row.original.id}`}
              >
                <div className="flex items-center">
                  <EyeIcon
                    aria-hidden="true"
                    className="mr-2 h-5 w-5 text-current"
                  />
                  <span className="block truncate">
                    View
                    <span className="sr-only">
                      , {props.row.original.vehicleServiceTask.name}
                    </span>
                  </span>
                </div>
              </Button>
            </AuthShield>
            <AuthShield access={["update-vehicles"]}>
              <Button
                variant="text"
                className={itemClasses}
                type="link"
                href={`/fleets/vehicles/service-reminders/${props.row.original.id}/edit`}
              >
                <div className="flex items-center">
                  <PencilIcon
                    aria-hidden="true"
                    className="mr-2 h-5 w-5 text-current"
                  />
                  <span className="block truncate">
                    Edit
                    <span className="sr-only">
                      , {props.row.original.vehicleServiceTask.name}
                    </span>
                  </span>
                </div>
              </Button>
            </AuthShield>
            <AuthShield access={["destroy-vehicles"]}>
              <Button
                variant="text"
                className={itemClasses}
                onClick={() => {
                  setAlert({
                    id: props.row.original.id!,
                    name: props.row.original.vehicleServiceTask.name,
                  });
                }}
              >
                <div className="flex items-center">
                  <TrashIcon
                    aria-hidden="true"
                    className="mr-2 h-5 w-5 text-current"
                  />
                  <span className="block truncate">
                    Delete
                    <span className="sr-only">
                      , {props.row.original.vehicleServiceTask.name}
                    </span>
                  </span>
                </div>
              </Button>
            </AuthShield>
          </ButtonDots>
        ),
      },
    ],
    []
  );

  const handleRefetch = useCallback(() => {
    setRefetching(true);
    refetch().finally(() => {
      setRefetching(false);
    });
  }, [refetch]);

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title={ServiceReminderListResource.name}
        heading={ServiceReminderListResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: ServiceReminderListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="mb-6 sm:flex sm:items-center md:mb-8">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">
              {t("heading_vehicle_service_reminder")}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {t("description_vehicle_service_reminder")}
            </p>
          </div>
          <div className="ml-auto flex items-center space-x-2 pr-3">
            <AuthShield access={["create-vehicles"]}>
              <Button type="link" href="/fleets/vehicles/service-reminders/add">
                <PlusCircleIcon className="mr-2 h-5 w-5" />
                Add service reminder
              </Button>
            </AuthShield>
            <Button
              variant="icon"
              onClick={handleRefetch}
              disabled={loading}
              className="flex h-9 items-center justify-center px-2 text-blue-700"
            >
              <ArrowPathIcon
                aria-hidden="true"
                className={classNames(
                  "h-5 w-5",
                  refetching ? "animate-spin" : ""
                )}
              />
              <span className="sr-only">Refresh service reminder list</span>
            </Button>
          </div>
        </div>

        <TableCursor
          data={serviceReminders}
          columns={columns}
          loading={loading}
          totalRows={serviceReminders.length}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          enableRowSelection={false}
          getCoreRowModel={getCoreRowModel()}
          enableGlobalFilter={true}
          fallbackTitle="No service reminders found"
          fallbackText="You can add a new service reminder by clicking on add service reminder button."
        />
      </div>
    </>
  );
};

export default ServiceReminderList;
export const ServiceReminderListResource: ResourceProps = {
  name: "Service Reminders",
  description: "Update an existing vehicle service reminders",
  access: ["update-vehicles"],
  path: "/service-reminders",
  icon: "bi bi-person",
};
